import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  ApplicationBodyFooter
} from '../../../components/application/defaultComponents'
import Button from '../../../components/basic/Button'
import { InsuranceTypeData, ApplicantData } from '../../../dtos/applicationData'
import { useApplication } from '../../../context/application'
import { tlieSources } from 'src/constants/tlie'
import { OnboardingSchema } from 'src/yup/NewAppSchema'
import InsuranceType from './InsuranceType'
import ApplicationType from './ApplicationType'
import PdfApplication from './PdfApplication'
import ApplicantName from './ApplicantName'
import ApplicantEmail from './ApplicantEmail'
import BeforeStart from './BeforeStart'
import loaderWhiteImg from 'src/assets/loader-white.svg'

const Onboarding = () => {
  const navigate = useNavigate()
  const { application, updateApplication, createApplicant } = useApplication()
  const [btnTakingAction, setBtnTakingAction] = useState(false)
  const [step, setStep] = useState(0)
  const [insuranceType, setInsuranceType] = useState<InsuranceTypeData>(() => application?.insuranceType ?? undefined)
  const [applicationMethod, setApplicationMethod] = useState<string>(() => {
    if (application?.applicant?.firstName || application?.applicant?.lastName || application?.applicant?.emailAddress)
      return 'ONLINE'

    return ''
  })
  const [applicantData, setApplicantData] = useState<Partial<ApplicantData>>(() => {
    return {
      firstName: application?.applicant?.firstName ?? '',
      lastName: application?.applicant?.lastName ?? '',
      isFirmAdministrator: application?.applicant?.isFirmAdministrator ?? false,
      emailAddress: application?.applicant?.emailAddress ?? ''
    }
  })
  const [confirmEmail, setConfirmEmail] = useState(application?.applicant?.emailAddress ?? '')
  const [isEmailVerificationValid, setIsEmailVerificationValid] = useState<boolean | undefined>(undefined)
  const [emailValidation, setEmailValidation] = useState({ active: false, title: '', subtitle: '' })

  const validateEmailVerification = useCallback(async () => {
    const validation = await OnboardingSchema.isValid({
      emailAddress: applicantData.emailAddress,
      confirmEmail
    })

    setIsEmailVerificationValid(validation)

    return validation
  }, [applicantData.emailAddress, confirmEmail])

  useEffect(() => {
    if (!!confirmEmail && applicantData?.emailAddress) {
      validateEmailVerification()
    }
  }, [validateEmailVerification, confirmEmail, applicantData])

  const steps = useMemo(() => {
    let defaultPages = [
      <InsuranceType insuranceType={insuranceType} setInsuranceType={setInsuranceType} />,
      <ApplicationType applicationMethod={applicationMethod} setApplicationMethod={setApplicationMethod} />
    ]

    switch (applicationMethod) {
      case 'ONLINE':
        return [
          ...defaultPages,
          <ApplicantName
            applicantData={applicantData}
            setApplicantData={setApplicantData}
            insuranceType={insuranceType}
          />,
          <ApplicantEmail
            applicantData={applicantData}
            setApplicantData={setApplicantData}
            confirmEmail={confirmEmail}
            setConfirmEmail={setConfirmEmail}
            isEmailVerificationValid={isEmailVerificationValid}
            setIsEmailVerificationValid={setIsEmailVerificationValid}
            emailValidation={emailValidation}
          />,
          <BeforeStart insuranceType={insuranceType} />
        ]
      case 'PDF':
        return [...defaultPages, <PdfApplication insuranceType={insuranceType} />]
      default:
        return defaultPages
    }
  }, [applicantData, applicationMethod, confirmEmail, emailValidation, insuranceType, isEmailVerificationValid])

  useEffect(() => {
    let currentStep = 0
    if (application?.insuranceType) {
      currentStep = 1
    }
    if (applicationMethod !== '') {
      currentStep = 2
    }
    if (application?.applicant?.firstName && application?.applicant?.lastName) {
      currentStep = 3
    }
    if (application?.applicant?.emailAddress) {
      currentStep = 4
    }
    setStep(currentStep)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateApplicationForm = useCallback(() => {
    const newData = { insuranceType, applicant: { ...applicantData } }
    updateApplication({ ...application, ...newData, stoppedAt: step === 4 ? '/firm/main' : '/new' })
  }, [applicantData, application, insuranceType, updateApplication, step])

  const pageValidation = async () => {
    switch (step) {
      case 0:
        return !!insuranceType
      case 1:
        return !!applicationMethod
      case 2:
        return !!applicantData.firstName && !!applicantData.lastName
      case 3:
        const validation = await validateEmailVerification()
        return validation
      default:
        return true
    }
  }

  const handleBack = () => {
    if (step > 0) setStep(prev => prev - 1)
    else navigate('/')
  }

  const handleCreateApplicant = async () => {
    updateApplicationForm()
    const { isSuccess, title, subtitle } = await createApplicant(applicantData)

    if (isSuccess === false) {
      setEmailValidation({
        active: true,
        title: title ?? '',
        subtitle: subtitle ?? ''
      })
      throw new Error()
    }
  }

  const handleNext = async () => {
    setBtnTakingAction(true)
    if (await pageValidation()) {
      if (step === steps.length - 1) {
        if (insuranceType === 'LAWYER') {
          navigate('/firm/main')
        } else {
          navigate('/judge/basic/main')
        }
      }
      else {
        try {
          if (step === 3) {
            await handleCreateApplicant()
          } else {
            updateApplicationForm()
          }
          window.scrollTo(0, 0)
          setStep(prev => prev + 1)
        } catch (error) { }
      }
    }
    setBtnTakingAction(false)
  }

  const forwardButton = applicationMethod === 'PDF' ? 'next' : (
    step === 4 ? undefined : (step > 1 ? 'saveContinue' : 'next')
  )

  const returnTLIEButton = applicationMethod === 'PDF' && step === 2

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        {steps[step]}
      </ApplicationBodyContent>
      <ApplicationBodyFooter marginTop={forwardButton ? undefined : '100px'}>
        <Button prebuilt='back' onClick={handleBack} disabled={btnTakingAction} />
        {returnTLIEButton ? (
          <Button
            prebuilt='returnTLIE'
            onClick={() => window.open(tlieSources.mainSiteUrl, '_self')}
          />
        ) : (
          <Button
            prebuilt={forwardButton}
            onClick={handleNext}
            minWidth={forwardButton ? undefined : '227px'}
            isTakingAction={btnTakingAction}
          >
            {btnTakingAction ? <img src={loaderWhiteImg} alt="loading" /> : 'Begin Application'}
          </Button>
        )}
      </ApplicationBodyFooter>
    </ApplicationBodyContainer>
  )
}

export default Onboarding